/** Versioned JSON persistence through the injected `Storage` (04 §10). An unreadable save is a fresh profile. */

import type { Storage } from './api';
import { stampOperationDogs } from './dogs';
import { freshProfile, type OperationState, type ProfileState, SAVE_VERSION } from './state';

export const SAVE_KEY = 'nightdrop.profile';

type Json = Record<string, unknown>;

/** Steps from version N to N + 1. None yet: version 1 is the first format. */
const MIGRATIONS: Record<number, (save: Json) => Json> = {};

/** Bring an older save up to the current format. Today this is the identity. */
export function migrate(save: Json): Json {
  let current = save;
  let version = typeof current['version'] === 'number' ? current['version'] : 0;
  while (version < SAVE_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) break;
    current = step(current);
    version += 1;
  }
  return current;
}

function isObject(v: unknown): v is Json {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const ARRAYS = ['unlocks', 'hall', 'retired', 'memorial', 'classicRoster', 'firsts', 'daily'] as const;
const NUMBERS = ['version', 'commendations', 'operationsWon', 'hazardUnlocked', 'nextRangerId', 'bestStreak'] as const;
const TABLES = ['bestScores', 'landings', 'archetypeStats', 'streaks', 'flights'] as const;

function validOperation(op: unknown): op is Json {
  return isObject(op) && Array.isArray(op['nodes']) && Array.isArray(op['company']) && Array.isArray(op['theatres'])
    && typeof op['rp'] === 'number' && typeof op['readiness'] === 'number' && typeof op['status'] === 'string';
}

/**
 * Fill in the fields src/meta adds to the contract, so a contract-only save - or one written
 * before a field existed - still loads. The profile's own additions default through `freshProfile`.
 */
function withOperationDefaults(op: Json): OperationState {
  const defaults = {
    deployed: null, rescueNodeId: null, stock: {}, intelHeld: [], revealedTheatres: [], forcedRolled: [],
    luckyUsed: [], cadetRestarts: [], inducted: 0, tagNodes: {}, trainablePerks: [],
  };
  return { ...defaults, ...op } as unknown as OperationState;
}

/** Parse, migrate and sanity-check a stored profile; null when it cannot be trusted. */
export function parseProfile(text: string | null): ProfileState | null {
  if (text === null || text === '') return null;
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (!isObject(raw) || typeof raw['version'] !== 'number' || raw['version'] > SAVE_VERSION) return null;
  const save = { ...freshProfile(), ...migrate(raw) } as Json;
  if (save['version'] !== SAVE_VERSION) return null;
  if (ARRAYS.some((k) => !Array.isArray(save[k])) || NUMBERS.some((k) => typeof save[k] !== 'number')) return null;
  if (TABLES.some((k) => !isObject(save[k]))) return null;
  if (save['dailyPending'] !== null && typeof save['dailyPending'] !== 'string') return null;
  if (save['operation'] !== null) {
    if (!validOperation(save['operation'])) return null;
    const op = withOperationDefaults(save['operation']);
    // ROUND8: a save from before the dogs has no counts on its nodes, and a briefing must not say "none" of a
    // map that will have some.
    stampOperationDogs(op);
    save['operation'] = op;
  }
  return save as unknown as ProfileState;
}

export function loadProfile(storage: Storage): ProfileState {
  let text: string | null = null;
  try {
    text = storage.load(SAVE_KEY);
  } catch {
    text = null;
  }
  return parseProfile(text) ?? freshProfile();
}

/** Writes the profile and returns the JSON, which the API keeps as its rollback point. */
export function saveProfile(storage: Storage, profile: ProfileState): string {
  const text = JSON.stringify(profile);
  storage.save(SAVE_KEY, text);
  return text;
}
